// ts-rest contract for the KPI Reporting platform — the upload-driven monthly
// management report built from six Tercero exports (vendor/septima-ontology-wealth/packages/ontology-wealth/specs/kpi/README.md).
//
// SEPARATE FROM `kpi.ts`, DELIBERATELY. That contract serves the LIVE figures
// computed from the ValuationGrid + Yahoo pricing, and it still has two
// consumers (Ops › Fees & Billing › AUM, View › Firm Overview).
// This one serves month-locked figures from uploaded evidence. They are
// different systems with different truth — as-at-month-end versus right-now —
// and merging them would make that distinction invisible at the call site.
//
// AGGREGATION HAPPENS SERVER-SIDE. Spec 8.1 requires Performance to be derived
// after aggregation at the displayed level, and the multi-month bridge (B/Fwd
// from the earliest selected month, C/Fwd from the latest) is a domain rule, not
// a rendering choice. Deriving it once next to the facts is also what keeps the
// dashboard and the CSV export agreeing to the penny.
//
// The manager rows carry their client rows nested, because the spec needs both
// an expanded manager (9.3) and the totals expanded to every underlying client.
// Concatenating `managers[].clients` for the latter is a pure concat with no
// arithmetic, so no derivation is duplicated in the browser.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodyWithCode as errorBody } from '../schemas/common';

const c = initContract();

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/**
 * The three permitted New Client Category values (spec 6).
 *
 * Spelled in full. Spec 12 is explicit that the internal shorthand
 * "Disc & Adv Managed" — which the legacy `REPORTING_CATEGORIES` in `kpi.ts`
 * still uses on the live surfaces — must never reach a user-facing label.
 */
export const KPI_CLIENT_CATEGORIES = [
    'Advisory',
    'Discretionary & Advisory Managed',
    'Execution Only',
] as const;
export const kpiClientCategorySchema = z.enum(KPI_CLIENT_CATEGORIES);
export type KpiClientCategory = z.infer<typeof kpiClientCategorySchema>;

/** The six upload slots, in display order (spec 3). */
export const KPI_FILE_TYPES = [
    'kpi_performance_dc',
    'kpi_performance_dgw',
    'kpi_flows_dc',
    'kpi_flows_dgw',
    'kpi_browse_dc',
    'kpi_browse_dgw',
] as const;
export const kpiFileTypeSchema = z.enum(KPI_FILE_TYPES);
export type KpiFileType = z.infer<typeof kpiFileTypeSchema>;

/** Metrics the chart's metric selector offers (spec 9.4, 11). */
export const KPI_METRIC_KEYS = [
    'aumCFwd',
    'netMonies',
    'income',
    'performance',
    'totalAccounts',
    'totalClients',
] as const;
export const kpiMetricKeySchema = z.enum(KPI_METRIC_KEYS);
export type KpiMetricKey = z.infer<typeof kpiMetricKeySchema>;

/** The three kinds of record requiring correction (spec 10.1). */
export const KPI_EXCEPTION_ISSUES = [
    'unmatched_client',
    'unrecognised_category',
    'new_transaction_type',
] as const;
export const kpiExceptionIssueSchema = z.enum(KPI_EXCEPTION_ISSUES);
export type KpiExceptionIssue = z.infer<typeof kpiExceptionIssueSchema>;

const kpiMonthSchema = z.string().regex(/^\d{4}-\d{2}$/);

// ---------------------------------------------------------------------------
// Metric bundle — the same shape at every reporting level
// ---------------------------------------------------------------------------

export const kpiMetricsSchema = z.object({
    /** Distinct non-blank Portfolio Name in scope. */
    totalAccounts: z.number().int().nonnegative(),
    /** Distinct non-blank Client Name in scope. */
    totalClients: z.number().int().nonnegative(),
    aumBFwd: z.number(),
    netMonies: z.number(),
    /** Displayed separately; never deducted from Performance (spec 8.1). */
    income: z.number(),
    /** aumCFwd − aumBFwd − netMonies, derived AFTER aggregation. */
    performance: z.number(),
    aumCFwd: z.number(),
});
export type KpiMetricsDto = z.infer<typeof kpiMetricsSchema>;

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/** One client under an expanded manager (spec 9.3). */
export const kpiClientRowSchema = kpiMetricsSchema.omit({ totalClients: true }).extend({
    clientKey: z.string(),
    clientName: z.string(),
    clientReference: z.string().nullable(),
    newClientCategory: kpiClientCategorySchema,
    investmentManager: z.string(),
    /** True when an operator's correction placed this client. */
    correctionApplied: z.boolean(),
});
export type KpiClientRowDto = z.infer<typeof kpiClientRowSchema>;

/** One row of the Manager Summary table (spec 9.2). */
export const kpiManagerRowSchema = kpiMetricsSchema.extend({
    managerKey: z.string(),
    investmentManager: z.string(),
    /** So the UI never has to string-compare against 'Unassigned'. */
    isUnassigned: z.boolean(),
    clients: z.array(kpiClientRowSchema),
});
export type KpiManagerRowDto = z.infer<typeof kpiManagerRowSchema>;

export const kpiCategoryRowSchema = kpiMetricsSchema.extend({
    newClientCategory: kpiClientCategorySchema,
});
export type KpiCategoryRowDto = z.infer<typeof kpiCategoryRowSchema>;

// ---------------------------------------------------------------------------
// GET /kpi-reporting/dashboard
// ---------------------------------------------------------------------------

export const kpiDashboardResponseSchema = z.object({
    /** False before any month has been rolled up — the UI renders an empty state. */
    available: z.boolean(),
    /** The months the filter actually resolved to, echoed back. */
    appliedMonths: z.array(kpiMonthSchema),
    /** The FILTERED total — the denominator for every displayed percentage (spec 9.1). */
    totals: kpiMetricsSchema,
    managers: z.array(kpiManagerRowSchema),
    categories: z.array(kpiCategoryRowSchema),
    /** Records excluded pending correction, so the shortfall is never silent. */
    unresolvedExceptionCount: z.number().int().nonnegative(),
    excludedClientCount: z.number().int().nonnegative(),
    excludedValue: z.number(),
});
export type KpiDashboardResponse = z.infer<typeof kpiDashboardResponseSchema>;

// ---------------------------------------------------------------------------
// GET /kpi-reporting/trend
// ---------------------------------------------------------------------------

export const kpiReportingTrendPointSchema = kpiMetricsSchema.extend({ month: kpiMonthSchema });
export type KpiReportingTrendPointDto = z.infer<typeof kpiReportingTrendPointSchema>;

export const kpiTrendResponseSchema = z.object({
    /** Oldest first. Each point is that month alone, never cumulative. */
    points: z.array(kpiReportingTrendPointSchema),
});
export type KpiTrendResponse = z.infer<typeof kpiTrendResponseSchema>;

// ---------------------------------------------------------------------------
// GET /kpi-reporting/my — the KPI figures of the signed-in IM. My Page was its
// web consumer, and My Page is retired (SEP-299). No page reads it now.
// ---------------------------------------------------------------------------

/** The figures of the IM's own book only. The ownership scope filters the rows. */
export const kpiReportingMyResponseSchema = z.object({
    /** False when no month has facts that the IM owns. */
    available: z.boolean(),
    /** The latest month with facts that the IM owns. Null when `available` is false. */
    latestMonth: kpiMonthSchema.nullable(),
    /** The metrics of that month alone. Null when `available` is false. */
    latest: kpiMetricsSchema.nullable(),
    /** Oldest first. At most 12 points, each one month alone. */
    trend: z.array(kpiReportingTrendPointSchema),
});
export type KpiReportingMyResponse = z.infer<typeof kpiReportingMyResponseSchema>;

// ---------------------------------------------------------------------------
// GET /kpi-reporting/report — the data of the PDF report
// ---------------------------------------------------------------------------

/** A manager row without its clients: the PDF report does not list clients. */
export const kpiReportManagerRowSchema = kpiManagerRowSchema.omit({ clients: true });
export type KpiReportManagerRowDto = z.infer<typeof kpiReportManagerRowSchema>;

export const kpiReportSectionSchema = z.object({
    /** Null for the Overall section. */
    newClientCategory: kpiClientCategorySchema.nullable(),
    /** Sorted descending by aumCFwd (spec 12). */
    managers: z.array(kpiReportManagerRowSchema),
    /** The Grand Total row of the section. */
    totals: kpiMetricsSchema,
});
export type KpiReportSectionDto = z.infer<typeof kpiReportSectionSchema>;

export const kpiReportResponseSchema = z.object({
    available: z.boolean(),
    appliedMonths: z.array(kpiMonthSchema),
    /** The labels of the managers in the manager filter. Empty means all managers. */
    managerFilterLabels: z.array(z.string()),
    /** The category filter. Empty means all categories. */
    categoryFilter: z.array(kpiClientCategorySchema),
    /** The Overall section first, then one section for each category in the filter. */
    sections: z.array(kpiReportSectionSchema),
    /**
     * Oldest first. At most 12 points. The series ends at the latest applied
     * month. The manager filter and the category filter apply.
     */
    trend: z.array(kpiReportingTrendPointSchema),
});
export type KpiReportResponse = z.infer<typeof kpiReportResponseSchema>;

// ---------------------------------------------------------------------------
// GET /kpi-reporting/filter-options
// ---------------------------------------------------------------------------

export const kpiFilterOptionsResponseSchema = z.object({
    /** Newest first. `ready` false while a month is still incomplete. */
    months: z.array(z.object({ month: kpiMonthSchema, ready: z.boolean() })),
    /**
     * Derived from the stored facts, NOT the staff roster: the KPI manager
     * population includes people who never had a Septima account and excludes
     * staff with no book, so the roster would list managers whose dashboard is
     * permanently empty (ADR-0009).
     */
    managers: z.array(
        z.object({
            managerKey: z.string(),
            investmentManager: z.string(),
            isUnassigned: z.boolean(),
        }),
    ),
    categories: z.array(kpiClientCategorySchema),
});
export type KpiFilterOptionsResponse = z.infer<typeof kpiFilterOptionsResponseSchema>;

// ---------------------------------------------------------------------------
// GET /kpi-reporting/months — upload progress + rollup lifecycle
// ---------------------------------------------------------------------------

export const kpiMonthFileSchema = z.object({
    fileType: kpiFileTypeSchema,
    label: z.string(),
    present: z.boolean(),
    rowCount: z.number().int().nonnegative().nullable(),
    uploadedAt: z.string().nullable(),
});
export type KpiMonthFileDto = z.infer<typeof kpiMonthFileSchema>;

export const kpiMonthStatusSchema = z.object({
    month: kpiMonthSchema,
    status: z.enum(['incomplete', 'processing', 'ready', 'failed']),
    filesPresent: z.number().int().min(0).max(6),
    filesExpected: z.literal(6),
    /** Always six entries, in catalogue order. */
    files: z.array(kpiMonthFileSchema),
    factCount: z.number().int().nonnegative(),
    exceptionCount: z.number().int().nonnegative(),
    unresolvedExceptionCount: z.number().int().nonnegative(),
    excludedClientCount: z.number().int().nonnegative(),
    excludedValue: z.number(),
    processedAt: z.string().nullable(),
    failureReason: z.string().nullable(),
});
export type KpiMonthStatusDto = z.infer<typeof kpiMonthStatusSchema>;

export const kpiMonthsResponseSchema = z.object({
    months: z.array(kpiMonthStatusSchema),
});
export type KpiMonthsResponse = z.infer<typeof kpiMonthsResponseSchema>;

// ---------------------------------------------------------------------------
// Exceptions (spec 10)
// ---------------------------------------------------------------------------

export const kpiExceptionRowSchema = z.object({
    id: z.string(),
    month: kpiMonthSchema,
    sourceFileType: z.string(),
    clientKey: z.string(),
    clientName: z.string(),
    clientReference: z.string().nullable(),
    issue: kpiExceptionIssueSchema,
    issueDetail: z.string(),
    /** The RAW source value, so the reviewer sees what Tercero actually said. */
    originalCategory: z.string().nullable(),
    originalInvestmentManager: z.string().nullable(),
    transactionType: z.string().nullable(),
    transactionValue: z.number().nullable(),
    /** Closing value held out — what resolving this row would add back. */
    excludedValue: z.number().nullable(),
    /** Never auto-applied; a human still confirms it (spec 10.2). */
    suggestedInvestmentManager: z.string().nullable(),
    suggestedCategory: kpiClientCategorySchema.nullable(),
    suggestionSource: z.string().nullable(),
    status: z.enum(['open', 'resolved']),
});
export type KpiExceptionRowDto = z.infer<typeof kpiExceptionRowSchema>;

export const kpiExceptionsResponseSchema = z.object({
    month: kpiMonthSchema,
    openCount: z.number().int().nonnegative(),
    resolvedCount: z.number().int().nonnegative(),
    /** Every manager already known for the month, for the assignment picker. */
    managerOptions: z.array(z.string()),
    rows: z.array(kpiExceptionRowSchema),
});
export type KpiExceptionsResponse = z.infer<typeof kpiExceptionsResponseSchema>;

/**
 * Both fields are required. 'Unassigned' is a legal manager value, so there is
 * no server-side defaulting and no ambiguity between "not supplied" and
 * "cleared" — the contract supplies exactly what the Action needs (ADR-0003).
 */
export const resolveKpiExceptionsBodySchema = z.object({
    month: kpiMonthSchema,
    resolutions: z
        .array(
            z.object({
                exceptionId: z.string().min(1),
                investmentManager: z.string().min(1),
                newClientCategory: kpiClientCategorySchema,
            }),
        )
        .min(1),
});
export type ResolveKpiExceptionsBody = z.infer<typeof resolveKpiExceptionsBodySchema>;

export const resolveKpiExceptionsResponseSchema = z.object({
    ok: z.literal(true),
    month: kpiMonthSchema,
    resolvedCount: z.number().int().nonnegative(),
    remainingOpenCount: z.number().int().nonnegative(),
});
export type ResolveKpiExceptionsResponse = z.infer<typeof resolveKpiExceptionsResponseSchema>;

// ---------------------------------------------------------------------------
// Upload envelope
//
// Multipart posts stay outside ts-rest, as every upload in this repo does
// (@ts-rest/nest's multipart story is rough — recon, cash, COAC and view
// performance all follow the same split). The response SHAPE is still declared
// here so the server validates against it and the web parses rather than
// duck-types it, which is the part of ADR-0003 that matters.
//
//   POST /api/kpi-reporting/uploads/:fileType?scope=YYYY-MM[&replace=true]
//
// Without `replace=true`, re-uploading a slot that already has data for the
// month returns 409 — so the confirmation prompt (spec 4) is enforced by the
// server rather than being a UI courtesy.
// ---------------------------------------------------------------------------

export const kpiUploadResultSchema = z.object({
    fileType: kpiFileTypeSchema,
    month: kpiMonthSchema,
    rowCount: z.number().int().nonnegative(),
    replaced: z.boolean(),
    duplicateOfPrevious: z.boolean(),
    /** The month's state after this upload, so the panel needs no refetch race. */
    monthStatus: kpiMonthStatusSchema,
});
export type KpiUploadResult = z.infer<typeof kpiUploadResultSchema>;

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export const kpiReportingContract = c.router(
    {
        /**
         * The dashboard for the current filter: totals, per-manager rows with
         * their clients nested, and per-category rows. Every figure is summed
         * from the stored facts with Performance derived after aggregation.
         *
         * All three filters are comma-joined; an absent filter means ALL.
         */
        getDashboard: {
            method: 'GET',
            path: '/kpi-reporting/dashboard',
            query: z
                .object({
                    months: z.string().optional(),
                    managers: z.string().optional(),
                    categories: z.string().optional(),
                })
                .optional(),
            responses: { 200: kpiDashboardResponseSchema },
            summary: 'Filtered KPI totals, manager rows with nested clients, and category rows.',
        },

        /**
         * Every metric per stored month, oldest first, for the manager/category
         * filter. Deliberately NOT month-filtered: the trend is the history.
         */
        getTrend: {
            method: 'GET',
            path: '/kpi-reporting/trend',
            query: z
                .object({
                    managers: z.string().optional(),
                    categories: z.string().optional(),
                })
                .optional(),
            responses: { 200: kpiTrendResponseSchema },
            summary: 'Per-month KPI history for the current manager/category filter.',
        },

        /**
         * The data of the PDF report: one section for Overall and one for each
         * category, and the 12-month trend that ends at the report month. The
         * server applies the manager labels, so the report, the CSV and the
         * page cannot disagree. The filters are the filters of `getDashboard`.
         */
        getReport: {
            method: 'GET',
            path: '/kpi-reporting/report',
            query: z
                .object({
                    months: z.string().optional(),
                    managers: z.string().optional(),
                    categories: z.string().optional(),
                })
                .optional(),
            responses: { 200: kpiReportResponseSchema },
            summary: 'Sections and trend for the KPI PDF report.',
        },

        /**
         * The KPI figures of the IM's own book. Served to Desk and to an
         * assistant for the IM. The rows come through `ctx.read`, so the
         * 'book-owner' scope keeps the book of every other IM out.
         */
        getMy: {
            method: 'GET',
            path: '/kpi-reporting/my',
            responses: { 200: kpiReportingMyResponseSchema },
            summary: "The latest month and the trend of the signed-in IM's own book.",
        },

        /** Month, manager and category options, derived from the stored facts. */
        getFilterOptions: {
            method: 'GET',
            path: '/kpi-reporting/filter-options',
            responses: { 200: kpiFilterOptionsResponseSchema },
            summary: 'Filter options for the KPI dashboard.',
        },

        /** Per-month six-slot upload progress and rollup lifecycle. */
        getMonths: {
            method: 'GET',
            path: '/kpi-reporting/months',
            responses: { 200: kpiMonthsResponseSchema },
            summary: 'Upload progress and processing state for every reporting month.',
        },

        /** The records requiring correction for one reporting month. */
        getExceptions: {
            method: 'GET',
            path: '/kpi-reporting/exceptions',
            query: z.object({ month: kpiMonthSchema }),
            responses: { 200: kpiExceptionsResponseSchema, 404: errorBody },
            summary: 'Records requiring correction for one reporting month.',
        },

        /**
         * Assign a manager and category to one or more excluded clients, for
         * this reporting month only, then rebuild the month.
         *
         * Takes a list because strict same-book matching produces a large and
         * repetitive exception set; confirming a thousand identical suggestions
         * one at a time is the same decision a thousand times.
         */
        resolveExceptions: {
            method: 'POST',
            path: '/kpi-reporting/exceptions/resolve',
            body: resolveKpiExceptionsBodySchema,
            responses: {
                200: resolveKpiExceptionsResponseSchema,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Record month-scoped corrections for excluded clients and rebuild the month.',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
